import { useEffect } from "react";
import { Cookie } from "../services/storage";
import { COOKIE_AD_ATKN, REDIRECT_URI } from "../constants/storage";
import {
  DISABLED_FEATURES,
  useFeatureToggle,
} from "../utils/feature-toggle/useFeatureToggle";
import { ROUTES } from "../configs/routes";
import { API_BASE_URL } from "../constants/api";
import { TURKEY_SA_COUNTRY_CODE } from "../constants/countryDefaults";
import { useSiteSettingsHook } from "./site-settings-hooks";
import { useAdssoLoginGuard } from "./adsso-login-guard-hooks";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "./query-hooks";
import { ApplicationState } from "../store";
import { UserState } from "../store/reducers/user-reducer";
import { useSelector } from "react-redux";
import { UtilsUser } from "../utils/UtilsUser";
import { useLayout } from "./layout-hooks";
import GoogleAnalytics from "../utils/GoogleAnalytics";
import i18next from "i18next";
import { useUserHooks } from "./user-hooks";
import { useMaintenance } from "./maintenance-page-hooks";
import { useLocale } from "./locale-hooks";
import { IS_LOCALHOST } from "../constants/environment";
import { SystemState } from "../store/reducers/site-reducer";
import { useLoginOptionHooks } from "./login-option-hooks";

export const useLayoutStartup = () => {
  // Hook declarations
  const { pathname } = useLocation();
  const history = useHistory();
  const query = useQuery();
  const { isDisabled } = useFeatureToggle();
  const { loadSystemProperties } = useSiteSettingsHook();
  const { ssoLoginRedirect } = useAdssoLoginGuard();
  const { removeEmailLoginSession } = useLoginOptionHooks();
  const { removeSelectedCountry } = useLayout();
  const { isLoading: isMaintenanceLoading } = useMaintenance();
  const { getUserData, loadUserCountryCode } = useUserHooks();
  const { loadLocale } = useLocale();

  // Redux Hooks
  const { isLoading: siteSettingIsLoading } = useSelector<
    ApplicationState,
    SystemState
  >((state) => state.system);
  const { userData } = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );

  const redirectUri = Cookie.get(REDIRECT_URI);

  // Helper functions (phase-independent side effects and checks)
  const redirectIfNoADSSOToken = () => {
    if (
      !isDisabled(DISABLED_FEATURES.ADSSO_SITE_RESTRICTION) &&
      !Cookie.get(COOKIE_AD_ATKN)
    ) {
      ssoLoginRedirect();
    }
  };

  const handleLoginDesignatedPageRedirect = () => {
    if (redirectUri && query.get("success")) {
      window.location.replace(`${API_BASE_URL}${redirectUri}`);
      Cookie.remove(REDIRECT_URI);
    }
  };

  const handleOldGRPSRedirect = () => {
    if (pathname === ROUTES.OLD_ABOUT_GRPS) {
      history.replace(ROUTES.ABOUT_GRPS);
    }
  };

  const handleRemoveTurkeySAUserCC = () => {
    if (
      userData &&
      userData.countryCode === TURKEY_SA_COUNTRY_CODE &&
      UtilsUser.isUserLoggedIn()
    ) {
      removeSelectedCountry();
    }
  };

  // Effect 1: Security gate (ADSSO) + parallel initialization
  // Runs after maintenance config loads
  useEffect(() => {
    if (isMaintenanceLoading) return;

    // Gate 1: ADSSO token verification (runs first)
    redirectIfNoADSSOToken();

    // Start system properties loading asynchronously (non-blocking)
    if (!isDisabled(DISABLED_FEATURES.SITE_SETTINGS)) {
      loadSystemProperties();
    }

    // Run non-dependent side effects in parallel
    handleLoginDesignatedPageRedirect();
    handleOldGRPSRedirect();
    handleRemoveTurkeySAUserCC();
  }, [isMaintenanceLoading]);

  // Effect 2: Coordinated initialization (GeoIP → Locale → User Profile)
  // Runs after system properties are ready (or skipped if disabled)
  useEffect(() => {
    // Gate: Wait for system properties to finish loading
    if (siteSettingIsLoading || isMaintenanceLoading) return;

    const executeStartup = async () => {
      try {
        // Phase 1: Load GeoIP with system property context
        await loadUserCountryCode();

        // Phase 2: Load locale (now with country code + system properties available)
        await loadLocale();

        // Phase 3: Load user profile (after locale is initialized)
        removeEmailLoginSession();
        if (UtilsUser.isUserLoggedIn()) {
          await getUserData();
        }
      } catch (error) {
        console.error("Error during startup initialization:", error);
        // Continue gracefully; page will render with fallback values
      }
    };

    executeStartup();
  }, [siteSettingIsLoading, isMaintenanceLoading]);

  // Effect 3: Google Analytics tracking on route changes
  useEffect(() => {
    if (IS_LOCALHOST) return;

    GoogleAnalytics.initialize(window.location.host);
    if (GoogleAnalytics.isInitialized) {
      GoogleAnalytics.sendPageView(pathname);
    }
  }, [pathname]);

  // Page access gate: wait for all critical initialization
  const hasPageAccess =
    i18next.isInitialized && !siteSettingIsLoading && !isMaintenanceLoading;

  return { hasPageAccess };
};
